import http from '@monolith/legacy/services/api/http';
import { captureBrandsException } from '@core/plugins/sentry/helper';
import type { AxiosResponse } from 'axios';
import type {
  CreateCampaignApiParams,
  CreateCampaignMethodParams,
  CampaignsTemplatesResponse,
  EditCampaignApiParams,
} from '../types/api.types';
import { ContactFilterType, ValueContactFilter } from '../../../../../modules/brands/domain/contact-management/types/api.types';
import { CampaignResource } from '../types/create-campaigns.types';

const ROOT_API_PATH = '/api/internal/v1/campaigns';

const jsonApiConfigs = {
  headers: {
    'Content-Type': 'application/vnd.api+json',
  },
};

export const getTemplates = async (): Promise<CampaignsTemplatesResponse[]> => {
  try {
    const { data }: AxiosResponse = await http().get(`/api/internal/v1/campaign-templates`, jsonApiConfigs);
    return data?.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'campaigns-management/api' },
      { label: 'action', value: 'getTemplates' },
    ]);
    return Promise.reject(error);
  }
};

export const getCampaign = async (campaignUuid: string): Promise<CampaignResource> => {
  try {
    const { data }: AxiosResponse = await http().get(`/api/internal/v1/campaigns/${campaignUuid}`, jsonApiConfigs);
    return data?.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'campaigns-management/api' },
      { label: 'action', value: 'getCampaign' },
    ]);
    return Promise.reject(error);
  }
};

export const createCampaign = async (
  { campaignName, filters, defaultLanguage, messages }: CreateCampaignMethodParams,
  startCampaign = true,
  campaignId?: string,
  scheduledAt?: string,
): Promise<CampaignResource> => {
  const abandonedCartFilter = {
    type: 'boolean' as ContactFilterType.Boolean,
    name: 'abandonedCartProspect',
    value: false,
  };

  const mergedFilters = [...Object.values(filters), abandonedCartFilter];
  for (let i = 0; i < mergedFilters.length; i++) {
    const filter = mergedFilters[i];
    // Remove 'custom' segment from filters as it's handled separately in the backend
    if (filter.name === 'segment' && filter.type === ContactFilterType.Value) {
      const valueFilter = filter as ValueContactFilter;
      if (Array.isArray(valueFilter.value) && valueFilter.value.some((value: string | number) => value === 'custom')) {
        mergedFilters.splice(i, 1);
        break;
      }
    }
  }

  try {
    const apiPayload: CreateCampaignApiParams | EditCampaignApiParams = {
      data: {
        type: 'campaign',
        id: campaignId,
        attributes: {
          name: campaignName,
          audience: {
            filters: mergedFilters,
          },
          content: {
            defaultLanguage,
            messages,
          },
        },
      },
    };

    const queryParams = new URLSearchParams({
      start_campaign: String(startCampaign),
    });

    if (scheduledAt) {
      queryParams.append('scheduled_at', scheduledAt);
    }

    const response: AxiosResponse<CampaignResource> = campaignId
      ? await http().patch(`${ROOT_API_PATH}/${campaignId}?${queryParams.toString()}`, apiPayload, jsonApiConfigs)
      : await http().post(`${ROOT_API_PATH}?${queryParams.toString()}`, apiPayload, jsonApiConfigs);

    return response.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'campaigns-management/api' },
      { label: 'action', value: 'createCampaign' },
    ]);
    return Promise.reject(error.response);
  }
};

export const scheduleCampaign = async (campaignId: string, scheduledAt: string): Promise<CampaignResource> => {
  try {
    const apiPayload = {
      data: {
        type: 'campaign' as const,
        id: campaignId,
        attributes: {
          scheduledAt,
        },
      },
    };

    const { data }: AxiosResponse = await http().post(`${ROOT_API_PATH}/${campaignId}/schedule`, apiPayload, jsonApiConfigs);
    return data?.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'campaigns-management/api' },
      { label: 'action', value: 'scheduleCampaign' },
    ]);
    throw error?.response ?? error;
  }
};

export const cancelScheduleCampaign = async (campaignId: string): Promise<void> => {
  try {
    await http().delete(`${ROOT_API_PATH}/${campaignId}/schedule`, jsonApiConfigs);
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'campaigns-management/api' },
      { label: 'action', value: 'cancelScheduleCampaign' },
    ]);
    throw error?.response ?? error;
  }
};

export const deleteDraft = async (campaignUuid: string): Promise<CampaignResource> => {
  try {
    const { data }: AxiosResponse = await http().delete(`/api/internal/v1/campaigns/${campaignUuid}`, {
      headers: {
        'Content-Type': 'application/vnd.api+json',
        Accept: 'application/vnd.api+json',
      },
    });
    return data?.data;
  } catch (error) {
    captureBrandsException(error, [
      { label: 'component', value: 'campaigns-management/api' },
      { label: 'action', value: 'deleteCampaignDraft' },
    ]);
    return Promise.reject(error);
  }
};
